import { calculateInsuredAmount, calculateInterest, calculateLoanAmount, pmt } from '../../financial/loan';
import { getRate, getSPPIRate } from './loanFilters';
import { daysInYear } from '../../utils/date';
var Loan = /** @class */ (function () {
    /**
     * @param {Array<SPPIRate>} SPPIRates
     * @param {Array<Rate>} rates
     * @param {LoanInput} input
     */
    function Loan(SPPIRates, rates, input) {
        this.SPPIRates = SPPIRates;
        this.rates = rates;
        this.input = input;
        this.daysInMonth = 30;
        this.percentDiffFirstLastPayment = 0.04999;
        this.sectionLeft = -1;
        this.sectionRight = 1;
        this.selectionAttempts = 20;
    }
    /**
     * @return ResultEnvelope<LoanOutput | OutputError>
     */
    Loan.prototype.calculate = function () {
        var start = new Date().getTime();
        var rateSPPI = 0;
        var amountSPPI = 0;
        var amount = this.input.amount;
        var resultGrid = {
            attempts: 0,
            loanRatio: 0,
            diffFirstLastPayment: 0,
            grid: [],
        };
        try {
            if (this.input.insurance) {
                rateSPPI = getSPPIRate(this.input.numberOfPayments, this.SPPIRates) / 100;
            }
            if (this.input.onHand) {
                amount = calculateLoanAmount(this.input.amount, this.input.numberOfPayments, rateSPPI);
            }
            if (this.input.insurance) {
                amountSPPI = calculateInsuredAmount(amount, this.input.numberOfPayments, rateSPPI);
            }
            resultGrid = this.selectionGrid(amount, new Date(this.input.dateStart.getTime()));
        }
        catch (e) {
            return {
                success: false,
                result: {
                    error: e,
                },
            };
        }
        return {
            success: true,
            result: {
                amount: amount,
                amountSPPI: amountSPPI,
                amountOnHand: amount - amountSPPI,
                time: (new Date().getTime() - start) / 1000,
                differentFirstLastPayment: Math.abs(resultGrid.diffFirstLastPayment),
                selectionAttempts: resultGrid.attempts,
                loanRatio: resultGrid.loanRatio,
                grid: resultGrid.grid,
            },
        };
    };
    /**
     * @param {LoanInput} input
     */
    Loan.prototype.setInput = function (input) {
        this.input = input;
    };
    /**
     * @param {number} amount
     * @param {Date} date
     */
    Loan.prototype.selectionGrid = function (amount, date) {
        var sectionLeft = this.sectionLeft;
        var sectionRight = this.sectionRight;
        var attempts = 0;
        var loanRatio = 0;
        var grid = this.calculateGrid(amount, new Date(date.getTime()), loanRatio);
        while (Math.abs(grid.diffFirstLastPayment) >= this.percentDiffFirstLastPayment) {
            var gridSectionLeft = this.calculateGrid(amount, new Date(date.getTime()), sectionLeft);
            if (Math.sign(grid.diffFirstLastPayment) !== Math.sign(gridSectionLeft.diffFirstLastPayment)) {
                sectionRight = loanRatio;
            }
            else {
                sectionLeft = loanRatio;
            }
            loanRatio = (sectionLeft + sectionRight) / 2;
            grid = this.calculateGrid(amount, new Date(date.getTime()), loanRatio);
            attempts++;
            if (attempts === this.selectionAttempts) {
                break;
            }
        }
        return {
            attempts: attempts,
            loanRatio: loanRatio,
            diffFirstLastPayment: grid.diffFirstLastPayment,
            grid: grid.grid,
        };
    };
    /**
     * @param {number} amount
     * @param {Date} date
     * @param {number} loanRatio
     */
    Loan.prototype.calculateGrid = function (amount, date, loanRatio) {
        var grid = [
            {
                rate: getRate(0, this.input, this.rates),
                datePayment: new Date(date.getTime()),
                debtStartOfMonth: amount,
                debtEndOfMonth: amount,
                paymentMonthly: 0,
                daysInYear: daysInYear(date),
                calculatedInterest: 0,
                interest: 0,
                principal: 0,
            },
        ];
        var paymentMonthly = pmt(amount, this.input.numberOfPayments, grid[0].rate, loanRatio);
        for (var period = 1; period <= this.input.numberOfPayments; period++) {
            var rate = getRate(period, this.input, this.rates);
            if (rate !== grid[period - 1].rate) {
                paymentMonthly = pmt(grid[period - 1].debtEndOfMonth, this.input.numberOfPayments - period + 1, rate, 0);
            }
            date.setMonth(date.getMonth() + 1);
            var calculatedInterest = calculateInterest(grid[period - 1].debtEndOfMonth, rate, daysInYear(date), this.daysInMonth);
            var sumCalculatedInterest = grid.reduce(function (sum, current) {
                return sum + current['calculatedInterest'];
            }, grid[0].calculatedInterest) + calculatedInterest;
            var sumInterest = grid.reduce(function (sum, current) {
                return sum + current['interest'];
            }, grid[0].interest);
            var interest = Math.min(paymentMonthly, sumCalculatedInterest - sumInterest);
            var principal = grid[period - 1].debtEndOfMonth;
            if (period < this.input.numberOfPayments) {
                principal = Math.min(paymentMonthly - interest, grid[period - 1].debtEndOfMonth);
            }
            var debtStartOfMonth = grid[period - 1].debtEndOfMonth;
            var debtEndOfMonth = debtStartOfMonth - principal;
            grid.push({
                rate: rate,
                datePayment: new Date(date.getTime()),
                paymentMonthly: interest + principal,
                debtStartOfMonth: debtStartOfMonth,
                debtEndOfMonth: debtEndOfMonth,
                daysInYear: daysInYear(date),
                calculatedInterest: calculatedInterest,
                interest: interest,
                principal: principal,
            });
        }
        return {
            diffFirstLastPayment: grid[grid.length - 1].paymentMonthly / grid[1].paymentMonthly - 1,
            grid: grid,
        };
    };
    return Loan;
}());
export default Loan;
